import QuillDelta from 'quill-delta';

/**
 * Converts Quill delta JSON (or Delta instance) to a single-line-friendly plain string.
 * @param delta Serialized Quill delta, JSON string, or `QuillDelta` instance
 * @returns Plain text with normalized whitespace
 */
export function quillDeltaToPlainText(delta: unknown): string {
	if (delta == null) {
		return '';
	}
	try {
		const raw = typeof delta === 'string' ? JSON.parse(delta) : delta;
		const instance = delta instanceof QuillDelta ? delta : new QuillDelta(raw as never);
		let text = '';
		for (const op of instance.ops ?? []) {
			if (typeof op.insert === 'string') {
				text += op.insert;
			} else if (op.insert != null) {
				text += ' ';
			}
		}
		return text.replace(/\s+/g, ' ').trim();
	} catch {
		return '';
	}
}

/**
 * @param text Source string
 * @param maxLength Maximum length before truncation (including ellipsis)
 * @returns Truncated string with an ellipsis when shortened
 */
export function truncatePlainText(text: string, maxLength: number): string {
	const t = text.trim();
	if (t.length <= maxLength) {
		return t;
	}
	return `${t.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
}

export const NOTE_LIST_SUMMARY_MAX_LENGTH = 220;

function stripLightMarkdown(input: string): string {
	return input
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		.replace(/[*_#>`]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * Plain-text preview for note list cards: transcription summary, then editable summary delta, then body.
 * @param note GraphQL note fields used for preview text
 * @param note.content Note body delta
 * @param note.transcription Optional transcription summary / editable summary
 * @returns Truncated plain-text preview
 */
export function buildNoteListSummaryPreview(note: {
	content?: unknown | null;
	transcription?: {
		summary?: string | null;
		editableSummary?: unknown | null;
	} | null;
}): string {
	const fromSummary = note.transcription?.summary?.trim();
	if (fromSummary) {
		return truncatePlainText(stripLightMarkdown(fromSummary), NOTE_LIST_SUMMARY_MAX_LENGTH);
	}
	const fromEditable = quillDeltaToPlainText(note.transcription?.editableSummary);
	if (fromEditable) {
		return truncatePlainText(fromEditable, NOTE_LIST_SUMMARY_MAX_LENGTH);
	}
	return truncatePlainText(quillDeltaToPlainText(note.content), NOTE_LIST_SUMMARY_MAX_LENGTH);
}
